<script setup>
import { ref, defineProps, onMounted, onBeforeUnmount} from 'vue'
import axios from "axios";
import Chart from 'chart.js/auto';

const props = defineProps(['field'])
const field = ref(props.field)

// 気象情報
const weather = ref(null);
const weatherHistory = ref(null);
const weatherCodeChar = [
  { codes: [ 0 ], img: 'bi-brightness-high', text: '快晴' },
  { codes: [ 1, 2 ], img: 'bi-cloud-sun', text: '晴れ' },
  { codes: [ 3, 45, 48 ], img: 'bi-cloud', text: '曇り' },
  { codes: [ 51, 53, 55, 56, 57 ], img: 'bi-cloud-drizzle', text: '霧雨' },
  { codes: [ 61, 63, 66, 80 ], img: 'bi-cloud-rain', text: '雨' },
  { codes: [ 65, 67, 81, 82 ], img: 'bi-cloud-rain-heavy', text: '大雨' },
  { codes: [ 71, 73, 77, 85 ], img: 'bi-cloud-snow', text: '雪' },
  { codes: [ 75, 86 ], img: 'bi-cloud-sleet', text: '大雪' },
  { codes: [ 95, 96, 99 ], img: 'bi-cloud-lightning-rain', text: '雷' },
]

const weatherChartDatas = ref([
  {
    labels: null,
    datasets: [
      {
        label: "温度",
        data: null,
        borderColor : "rgba(255,127,127,1)",
        backgroundColor : "rgba(255,193,193,1)",
        yAxisID: "y-axis-1",
      },
      {
        label: "相対湿度",
        data: null,
        borderColor : "rgba(127,127,255,1)",
        backgroundColor : "rgba(193,193,255,1)",
        yAxisID: "y-axis-2",
      },
    ]
  },
  {
    labels: null,
    datasets: [
      {
        label: "土壌水分：0～1cm",
        data: null,
        borderColor : "rgba(127,255,255,1)",
        backgroundColor : "rgba(173,255,255,1)",
      },
      {
        label: "土壌水分：1～3cm",
        data: null,
        borderColor : "rgba(127,191,255,1)",
        backgroundColor : "rgba(173,214,255,1)",
      },
      {
        label: "土壌水分：3～9cm",
        data: null,
        borderColor : "rgba(127,127,255,1)",
        backgroundColor : "rgba((173,173,255,1)",
      },
      {
        label: "土壌水分：9～27cm",
        data: null,
        borderColor : "rgba(191,127,255,1)",
        backgroundColor : "rgba(214,173,255,1)",
      },
      {
        label: "土壌水分：27～81cm",
        data: null,
        borderColor : "rgba(255,127,255,1)",
        backgroundColor : "rgba(255,173,255,1)",
      }
    ]
  },
  {
    labels: null,
    datasets: [
      {
        label: "直接日射",
        data: null,
        borderColor : "rgba(255,127,127,1)",
        backgroundColor : "rgba(255,193,193,1)",
      },
      {
        label: "拡散日射",
        data: null,
        borderColor : "rgba(255,191,127,1)",
        backgroundColor : "rgba(255,221,188,1)",
      },
      {
        label: "地上太陽放射",
        data: null,
        borderColor : "rgba(255,255,127,1)",
        backgroundColor : "rgba(255,255,188,1)",
      }
    ]
  },
  {
    labels: null,
    datasets: [
      {
        type: 'line',
        label: "最高気温",
        data: null,
        borderColor : "rgba(254,97,132,0.8)",
        backgroundColor : "rgba(254,97,132,0.5)",
        yAxisID: "y-axis-1",
      },
      {
        type: 'line',
        label: "最低気温",
        data: null,
        borderColor : "rgba(54,164,235,0.8)",
        backgroundColor : "rgba(54,164,235,0.5)",
        yAxisID: "y-axis-1",
      },
      {
        type: 'bar',
        label: "降水量",
        data: null,
        borderColor : "rgba(127,127,255,1)",
        backgroundColor : "rgba(193,193,255,1)",
        yAxisID: "y-axis-2",
      },
    ]
  },
])

const weatherChartOptions = ref([
  {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
      title: {
        display: true,
        text: "本日：気温(℃) / 湿度(%)"
      }
    },
    scales: {
      "y-axis-1": {
        type: "linear",
        position: "left"
      },
      "y-axis-2": {
        type: "linear",
        position: "right"
      },
    }
  },
  {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
      title: {
        display: true,
        text: "本日：土壌湿度(㎥/㎥)"
      }
    },
    scales: {
    }
  },
  {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
      title: {
        display: true,
        text: "本日：日射(W/㎡)"
      }
    },
    scales: {
    }
  },
  {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
      title: {
        display: true,
        text: "気象履歴（直近１か月）"
      }
    },
    scales: {
      "y-axis-1": {
        type: "linear",
        position: "left"
      },
      "y-axis-2": {
        type: "linear",
        position: "right"
      },
    }
  }
])

let Charts = [
  {id: "c1", chart: null},
  {id: "c2", chart: null},
  {id: "c3", chart: null},
  {id: "c4", chart: null},
]

onMounted(() => {
  if (field?.value) {
    reloadChart(field.value.LatLng);
    reloadHistoryChart(field.value.LatLng);
  }
})

onBeforeUnmount(() => {
  // 生成済みのグラフを削除
  Charts.forEach(function(c){
    if (c.chart) {
      c.chart.destroy();
    }
  })
})

// グラフ再描画処理
function reloadChart(latlng) {
  // 気象情報取得（日）
  axios
  .get(
    "https://api.open-meteo.com/v1/forecast?"
      + "latitude=" + latlng.lat
      + "&longitude=" + latlng.lng
      + "&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_probability_max"
      + "&hourly=temperature_2m,relativehumidity_2m,direct_radiation,diffuse_radiation,terrestrial_radiation"
      + ",soil_moisture_0_1cm,soil_moisture_1_3cm,soil_moisture_3_9cm,soil_moisture_9_27cm,soil_moisture_27_81cm"
      + "&timezone=Asia%2FTokyo&forecast_days=7"
  )
  .then((response) => {
    weather.value = response.data;

    // hourlyの当日分以外のデータを切り捨て
    weather.value.hourly.time.splice(24, weather.value.hourly.time.length - 24);
    weather.value.hourly.temperature_2m.splice(24, weather.value.hourly.temperature_2m.length - 24);
    weather.value.hourly.relativehumidity_2m.splice(24, weather.value.hourly.relativehumidity_2m.length - 24);
    weather.value.hourly.direct_radiation.splice(24, weather.value.hourly.direct_radiation.length - 24);
    weather.value.hourly.diffuse_radiation.splice(24, weather.value.hourly.diffuse_radiation.length - 24);
    weather.value.hourly.terrestrial_radiation.splice(24, weather.value.hourly.terrestrial_radiation.length - 24);
    weather.value.hourly.soil_moisture_0_1cm.splice(24, weather.value.hourly.soil_moisture_0_1cm.length - 24);
    weather.value.hourly.soil_moisture_1_3cm.splice(24, weather.value.hourly.soil_moisture_1_3cm.length - 24);
    weather.value.hourly.soil_moisture_3_9cm.splice(24, weather.value.hourly.soil_moisture_3_9cm.length - 24);
    weather.value.hourly.soil_moisture_9_27cm.splice(24, weather.value.hourly.soil_moisture_9_27cm.length - 24);
    weather.value.hourly.soil_moisture_27_81cm.splice(24, weather.value.hourly.soil_moisture_27_81cm.length - 24);

    // 時刻フォーマット（暫定）
    for (let idx in weather.value.hourly.time) {
      weather.value.hourly.time[idx] = (new Date(weather.value.hourly.time[idx]).toLocaleTimeString('ja-JP')).toString().slice(0, -3)
    }

    // 温度／湿度
    weatherChartDatas.value[0].labels = weather.value.hourly.time;
    weatherChartDatas.value[0].datasets[0].data = weather.value.hourly.temperature_2m,
    weatherChartDatas.value[0].datasets[1].data = weather.value.hourly.relativehumidity_2m,
    drawChart(0, "line", weatherChartDatas.value[0], weatherChartOptions.value[0]);

    // 土壌湿度
    weatherChartDatas.value[1].labels = weather.value.hourly.time;
    weatherChartDatas.value[1].datasets[0].data = weather.value.hourly.soil_moisture_0_1cm,
    weatherChartDatas.value[1].datasets[1].data = weather.value.hourly.soil_moisture_1_3cm,
    weatherChartDatas.value[1].datasets[2].data = weather.value.hourly.soil_moisture_3_9cm,
    weatherChartDatas.value[1].datasets[3].data = weather.value.hourly.soil_moisture_9_27cm,
    weatherChartDatas.value[1].datasets[4].data = weather.value.hourly.soil_moisture_27_81cm,
    drawChart(1, "line", weatherChartDatas.value[1], weatherChartOptions.value[1]);

    // 日射
    weatherChartDatas.value[2].labels = weather.value.hourly.time;
    weatherChartDatas.value[2].datasets[0].data = weather.value.hourly.direct_radiation,
    weatherChartDatas.value[2].datasets[1].data = weather.value.hourly.diffuse_radiation,
    weatherChartDatas.value[2].datasets[2].data = weather.value.hourly.terrestrial_radiation,
    drawChart(2, "line", weatherChartDatas.value[2], weatherChartOptions.value[2]);
  })
  .catch((error) => {
      console.error("APIリクエストエラー:", error);
  });
}

// 履歴グラフ再描画処理
function reloadHistoryChart(latlng) {
  var dt = new Date();
  var dateTo = new Date(dt.setDate(dt.getDate() - 7));
  var dateFrom = new Date(dt.setDate(dt.getDate() - 30));

  // 気象情報取得
  axios
  .get(
    "https://archive-api.open-meteo.com/v1/archive?"
      + "latitude=" + latlng.lat
      + "&longitude=" + latlng.lng
      + "&start_date=" + dateFrom.toLocaleDateString('sv-SE')
      + "&end_date=" + dateTo.toLocaleDateString('sv-SE')
      + "&daily=temperature_2m_max,temperature_2m_min,precipitation_sum"
      + "&timezone=Asia%2FTokyo"
  )
  .then((response) => {
    weatherHistory.value = response.data;

    // 時刻フォーマット（暫定）
    for (let idx in weatherHistory.value.daily.time) {
      weatherHistory.value.daily.time[idx] = (weatherHistory.value.daily.time[idx]).toString().slice(5).replace('-', '/')
    }

    weatherChartDatas.value[3].labels = weatherHistory.value.daily.time;
    weatherChartDatas.value[3].datasets[0].data = weatherHistory.value.daily.temperature_2m_max,
    weatherChartDatas.value[3].datasets[1].data = weatherHistory.value.daily.temperature_2m_min,
    weatherChartDatas.value[3].datasets[2].data = weatherHistory.value.daily.precipitation_sum,
    drawChart(3, null, weatherChartDatas.value[3], weatherChartOptions.value[3]);
  })
  .catch((error) => {
      console.error("APIリクエストエラー:", error);
  });
}

// グラフ描画処理
function drawChart(idx, type, data, options) {
  // グラフ生成
  var ctx = document.getElementById("c"+(idx+1).toString()).getContext('2d');
  Charts[idx].chart = new Chart(ctx, {
    type: type,
    data: data,
    options: options
  });

  // キャンバスの高さを変更
  ctx.canvas.height = "100%";
}
</script>

<template>
  <a class="d-flex justify-content-center text-primary-emphasis bg-light" href="https://open-meteo.com/" target="_blank">Open-Meteo -Free Weather API-</a>

  <table class="table table-sm table-bordered">
    <thead>
      <tr class="table-light">
        <th></th>
        <th class="text-center" scope="col" v-for="time in weather?.daily?.time" :key="time">
          {{ time.slice(5, 7) }}/{{ time.slice(8, 10) }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td class="text-center align-middle table-light">天気</td>
        <td class="text-center" v-for="(code, index) in weather?.daily?.weathercode" :key="index">
          <i :class="`bi ${ weatherCodeChar.find(data => data.codes.includes(code)).img }`" id="icon"></i>
          <div>{{ weatherCodeChar.find(data => data.codes.includes(code)).text }}</div>
        </td>
      </tr>
      <tr>
        <td class="text-center align-middle table-light" rowspan="2">気温<br>(℃)</td>
        <td class="text-center" v-for="(max, index) in weather?.daily?.temperature_2m_max" :key="index">{{ max }}</td>
      </tr>
      <tr>
        <td class="text-center" v-for="(min, index) in weather?.daily?.temperature_2m_min" :key="index">{{ min }}</td>
      </tr>
      <tr>
        <td class="text-center align-middle table-light">降水<br>(%)</td>
        <td class="text-center" v-for="(precipitation, index) in weather?.daily?.precipitation_probability_max" :key="index">{{ precipitation }}</td>
      </tr>
    </tbody>
  </table>

  <div class="canvas-container">
    <canvas class="border" id="c1"></canvas>
    <canvas class="border" id="c2"></canvas>
    <canvas class="border" id="c3"></canvas>
    <canvas class="border" id="c4"></canvas>
  </div>
</template>

<style scoped>
/* 天気アイコン */
i {
  font-size: 3rem;
  color: cornflowerblue;
}
table * {
  padding: 0px !important;
}
@media (max-width: 767px) {
  table * {
    font-size: 16px;
    #icon {
      font-size: 40px;
    }
  }
}
</style>
